import {
  DEFAULT_APP_TIMEZONE_PREFERENCE,
  LOCAL_BROWSER_TIMEZONE_MODE,
  TIMEZONE_OPTIONS,
  type AllowedTimezone,
  type AppTimezonePreference,
  type SelectedTimezone,
  type TimeFormat,
  type TimezoneMode,
  type TimezoneOption,
} from './timezone.constants'

// UTC is guaranteed to exist in TIMEZONE_OPTIONS
const UTC_OPTION = TIMEZONE_OPTIONS.find((o) => o.value === 'UTC') as TimezoneOption

/**
 * Get current offset for a timezone (e.g., "GMT-5" or "GMT+0")
 */
export function getTimezoneOffset(timezone: string): string {
  try {
    const now = new Date()
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    })
    const parts = formatter.formatToParts(now)
    const offsetPart = parts.find((p) => p.type === 'timeZoneName')
    return offsetPart?.value ?? 'GMT'
  } catch {
    return 'GMT'
  }
}

/**
 * Get user's browser timezone with SSR/test safety
 */
function getBrowserTimezoneCandidate(): string {
  let timezone = ''
  try {
    timezone = typeof Intl === 'undefined' ? '' : (Intl.DateTimeFormat().resolvedOptions().timeZone ?? '')
  } catch {
    timezone = ''
  }
  return timezone
}

export function getBrowserTimezoneValue(): string {
  return getBrowserTimezoneCandidate()
}

export function getBrowserTimezone(): string {
  return getBrowserTimezoneValue() || 'UTC'
}

/**
 * Find timezone option by value, with fallback for unknown values
 */
export function findTimezoneOption(timezone: string | null): TimezoneOption {
  const tz = timezone ?? 'UTC'
  return TIMEZONE_OPTIONS.find((o) => o.value === tz) ?? UTC_OPTION
}

/**
 * Build display label for dropdown options
 */
export function getDropdownLabel(option: TimezoneOption, browserTimezone: string): string {
  const offset = getTimezoneOffset(option.value)
  const base = `${option.label} (${offset})`
  return option.value === browserTimezone ? `${base} - From local settings` : base
}

/**
 * Build display label for current timezone (used in bloc description)
 * Returns just the display name, e.g., "Eastern Time"
 */
export function getCurrentTimezoneLabel(timezone: string | null): string {
  const option = findTimezoneOption(timezone)
  return option.displayName
}

/**
 * Get numeric UTC offset for a timezone (e.g., "−5" or "+0")
 */
export function getNumericUtcOffset(timezone: string): string {
  try {
    const now = new Date()
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    })
    const parts = formatter.formatToParts(now)
    const offsetPart = parts.find((p) => p.type === 'timeZoneName')
    const offset = offsetPart?.value ?? 'GMT'
    // Convert "GMT-5" to "−5", "GMT+0" to "+0"
    return offset.replace('GMT', '').replace('-', '−') || '+0'
  } catch {
    return '+0'
  }
}

/**
 * Get full timezone description for display
 * e.g., "All times are displayed in Eastern Time (ET, UTC−5)"
 */
export function getTimezoneDescription(timezone: string | null): string {
  const option = findTimezoneOption(timezone)
  const utcOffset = getNumericUtcOffset(option.value)
  return `All times are displayed in ${option.displayName} (${option.abbreviation}, UTC${utcOffset})`
}

export function getAppTimezoneLabel(preference = DEFAULT_APP_TIMEZONE_PREFERENCE): string {
  const timezone = resolveTimezoneForDisplay(preference)
  const option = findTimezoneOption(timezone)

  if (option.value !== timezone) return timezone
  if (timezone === 'UTC') return 'UTC'
  return `${option.displayName} (${option.abbreviation})`
}

export function getAppTimezoneDisplayLabel(preference?: AppTimezonePreference): string {
  if (!preference) return ''

  const timezone = resolveTimezoneForDisplay(preference)
  if (timezone === 'UTC') return 'UTC'

  const option = findTimezoneOption(timezone)
  if (option.value !== timezone) return `${timezone} (UTC${getNumericUtcOffset(timezone)})`
  return `${option.abbreviation} (UTC${getNumericUtcOffset(option.value)})`
}

/**
 * Get the short timezone code for the active preference (e.g. "UTC", "ET", "PT").
 * Falls back to "LOCAL" for a browser-local timezone outside the supported set; the
 * expanded sidebar and popover still surface the precise zone and offset. This branch is
 * only reachable via LOCAL_BROWSER mode, since IANA-mode timezones are constrained to the
 * supported set during normalization.
 */
export function getAppTimezoneAbbreviation(preference = DEFAULT_APP_TIMEZONE_PREFERENCE): string {
  const timezone = resolveTimezoneForDisplay(preference)
  const option = findTimezoneOption(timezone)

  if (option.value === timezone) return option.abbreviation
  return 'LOCAL'
}

export function isAllowedTimezone(value: string): value is AllowedTimezone {
  return TIMEZONE_OPTIONS.some((option) => option.value === value)
}

export function isValidSelectedTimezone(value: string): value is SelectedTimezone {
  if (!value || value === LOCAL_BROWSER_TIMEZONE_MODE) return false
  if (value === 'UTC') return true

  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value })
    return true
  } catch {
    return false
  }
}

export function isTimeFormat(value: string): value is TimeFormat {
  return value === '12h' || value === '24h'
}

function isTimezoneMode(value: string): value is TimezoneMode {
  return value === 'UTC' || value === LOCAL_BROWSER_TIMEZONE_MODE || value === 'IANA'
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

export function normalizeAppTimezonePreference(value: unknown): AppTimezonePreference {
  if (!isRecord(value)) return DEFAULT_APP_TIMEZONE_PREFERENCE

  const timezoneMode =
    typeof value.timezoneMode === 'string' && isTimezoneMode(value.timezoneMode)
      ? value.timezoneMode
      : DEFAULT_APP_TIMEZONE_PREFERENCE.timezoneMode
  const timeFormat =
    typeof value.timeFormat === 'string' && isTimeFormat(value.timeFormat)
      ? value.timeFormat
      : DEFAULT_APP_TIMEZONE_PREFERENCE.timeFormat
  const timezone =
    typeof value.timezone === 'string' && isAllowedTimezone(value.timezone)
      ? value.timezone
      : DEFAULT_APP_TIMEZONE_PREFERENCE.timezone

  if (timezoneMode === 'UTC' || timezoneMode === LOCAL_BROWSER_TIMEZONE_MODE) {
    return { timezoneMode, timezone: 'UTC', timeFormat }
  }

  return { timezoneMode, timezone, timeFormat }
}

export function resolveTimezoneForDisplay(preference: AppTimezonePreference): string {
  const normalizedPreference = normalizeAppTimezonePreference(preference)

  if (normalizedPreference.timezoneMode === LOCAL_BROWSER_TIMEZONE_MODE) {
    const browserTimezone = getBrowserTimezoneValue()
    return browserTimezone && isValidSelectedTimezone(browserTimezone) ? browserTimezone : 'UTC'
  }

  if (normalizedPreference.timezoneMode === 'UTC') {
    return 'UTC'
  }

  return normalizedPreference.timezone
}

export function resolveSelectedTimezoneHeader(
  preference: AppTimezonePreference
): SelectedTimezone | undefined {
  const normalizedPreference = normalizeAppTimezonePreference(preference)

  if (normalizedPreference.timezoneMode === LOCAL_BROWSER_TIMEZONE_MODE) {
    const browserTimezone = getBrowserTimezoneValue()
    return browserTimezone && isValidSelectedTimezone(browserTimezone) ? browserTimezone : undefined
  }

  if (normalizedPreference.timezoneMode === 'UTC') {
    return 'UTC'
  }

  return normalizedPreference.timezone
}

export function getTimezonePreferenceQueryKeyPart(
  preference: AppTimezonePreference
): readonly [TimezoneMode, string, TimeFormat] {
  const normalizedPreference = normalizeAppTimezonePreference(preference)
  return [
    normalizedPreference.timezoneMode,
    resolveTimezoneForDisplay(normalizedPreference),
    normalizedPreference.timeFormat,
  ] as const
}
